import { renameKey } from './integrationUtils';
import { fetchWithAuth } from '../../../utils/apiHelpers';

/**
 * Service for handling integration API calls
 */
export const IntegrationApiService = {
  /**
   * Fetch all integrations
   * @returns {Promise} - Promise with integration data
   */
  fetchIntegrations: async () => {
    try {
      const response = await fetchWithAuth('/api/auth_update/', {
        method: 'GET',
      });
      return await response.json();
    } catch (err) {
      console.error('Error fetching integrations:', err);
      throw new Error('Error occurred while fetching integrations.');
    }
  },

  /**
   * Connect authentication for an integration
   * @param {String} id - Integration ID
   * @returns {Promise} - Promise with connection result
   */
  connectAuth: async id => {
    try {
      const data = new FormData();
      data.append('integration_id', id);
      localStorage.setItem('integration_id', id);

      const response = await fetchWithAuth('/api/connect_auth/', {
        method: 'POST',
        body: data,
      });
      return await response.json();
    } catch (err) {
      console.error('Error connecting auth:', err);
      throw new Error('Error occurred while connecting authentication.');
    }
  },

  /**
   * Disconnect authentication for an integration
   * @param {String} id - Integration ID
   * @returns {Promise} - Promise with disconnection result
   */
  disconnectAuth: async id => {
    try {
      const data = new FormData();
      data.append('integration_id', id);
      data.append('callback_url', `${window.location.origin}/callback`);
      localStorage.setItem('integration_id', id);

      const response = await fetchWithAuth('/api/disconnect_auth/', {
        method: 'POST',
        body: data,
      });
      return await response.json();
    } catch (err) {
      console.error('Error disconnecting auth:', err);
      throw new Error('Error occurred while disconnecting authentication.');
    }
  },

  /**
   * Handle Inuit authentication
   * @param {String} id - Integration ID
   * @returns {Promise} - Promise with auth URL
   */
  handleInuitAuth: async id => {
    try {
      const data = new FormData();
      data.append('client_id', '');
      data.append('secret_key', '');
      data.append('inuit_company_id', '');
      data.append('type', '');
      data.append('integration_id', id);
      data.append('callback_url', `${window.location.origin}/callback`);
      localStorage.setItem('integration_id', id);

      const response = await fetchWithAuth('/api/inuit_auth/', {
        method: 'POST',
        body: data,
      });
      return await response.json();
    } catch (err) {
      console.error('Error handling Inuit auth:', err);
      throw new Error('Error occurred during authentication.');
    }
  },

  /**
   * Add a new integration
   * @param {Object} formData - Integration form data
   * @returns {Promise} - Promise with new integration data
   */
  addIntegration: async formData => {
    try {
      const response = await fetchWithAuth('/api/add_integration/', {
        method: 'POST',
        body: formData,
      });
      return await response.json();
    } catch (err) {
      console.error('Error adding integration:', err);
      throw new Error('Error occurred while adding integration.');
    }
  },

  /**
   * Edit an existing integration
   * @param {Object} formData - Integration form data
   * @returns {Promise} - Promise with edited integration data
   */
  editIntegration: async formData => {
    try {
      const response = await fetchWithAuth('/api/edit_integration/', {
        method: 'POST',
        body: formData,
      });
      return await response.json();
    } catch (err) {
      console.error('Error editing integration:', err);
      throw new Error('Error occurred while editing integration.');
    }
  },

  /**
   * Fetch country data
   * @returns {Promise} - Promise with country data
   */
  fetchCountry: async () => {
    try {
      const response = await fetchWithAuth('/api/country/', {
        method: 'GET',
      });
      const data = await response.json();

      // Transform data for SelectPicker
      data.forEach(obj => {
        obj.country_currency = obj.country_label + ' (' + obj.currency + ') ';
      });
      data.forEach(obj => renameKey(obj, 'country_currency', 'label'));
      data.forEach(obj => renameKey(obj, 'country_id', 'value'));

      return data;
    } catch (err) {
      console.error('Error fetching countries:', err);
      throw new Error('Error occurred while fetching countries.');
    }
  },
};
